/**
 * Modale affichée quand le pipeline de génération de fiche échoue en aval du
 * consume-crédit. Reprend la structure visuelle de `CreditsExhaustedModal`
 * pour rester cohérent avec l'UX d'interruption existante.
 *
 * `refundState` reflète le statut du remboursement automatique :
 *   - "pending"  → refund en cours (on affiche un spinner discret)
 *   - "refunded" → crédit rendu, on peut rassurer l'user
 *   - "failed"   → refund KO (réseau/backend) — on le dit honnêtement,
 *                  le user doit nous contacter. Rare.
 *   - "skipped"  → consume n'avait pas abouti, rien à rendre
 */

import type { ReactNode } from "react";
import { Button, Card, Pill } from "./ui";
import { Ic } from "./icons";

export type RefundState = "pending" | "refunded" | "failed" | "skipped";

type Props = {
  /** Libellé de l'étape qui a échoué (ex. "Extraction du règlement applicable"). */
  stageLabel: string;
  /** Message brut retourné par le pipeline — affiché en petit pour contexte. */
  errorMessage: string;
  /** Code d'erreur technique — affiché en monospace sous le message. */
  errorCode: string;
  refundState: RefundState;
  onRetry: () => void;
  onClose: () => void;
  /** Quand fourni ET que l'errorCode l'autorise (zone-not-published /
   *  zone-not-found), un CTA primaire propose à l'user de fournir lui-même
   *  le règlement + plan graphique. Cf. `PluUploadModal`. */
  onUploadFallback?: () => void;
};

/**
 * Présentation user-facing dérivée du code d'erreur. Pour les cas permanents
 * (commune RNU, document non publié) on cache le bouton "Réessayer" car
 * réessayer ne changera rien — autant guider l'utilisateur vers la bonne
 * action (consulter mairie, etc.) plutôt que de l'inviter à brûler un retry.
 */
type Presentation = {
  pillLabel: string;
  title: string;
  body: ReactNode;
  hideRetry?: boolean;
  /** Si vrai ET que `onUploadFallback` est fourni, on affiche un CTA primaire
   *  "Uploader le règlement et le plan" — l'autre option (Annuler) reste
   *  visible. Pour les erreurs où l'upload manuel est la voie de sortie. */
  allowUploadFallback?: boolean;
};

function presentationFor(code: string, stageLabel: string): Presentation {
  switch (code) {
    case "commune-rnu":
      return {
        pillLabel: "Pas de PLU",
        title: "Cette commune n'a pas de PLU",
        body: (
          <>
            Elle est régie par le{" "}
            <strong>Règlement National d'Urbanisme (RNU)</strong>. Il n'y a
            donc pas de plan local d'urbanisme à analyser ici. Vous pouvez
            consulter les règles applicables sur{" "}
            <a
              href="https://www.service-public.fr/particuliers/vosdroits/F1986"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-ink-900"
            >
              service-public.fr
            </a>
            .
          </>
        ),
        hideRetry: true,
      };
    case "zone-not-published":
      return {
        pillLabel: "Document non publié",
        title: "Document d'urbanisme non publié",
        body: (
          <>
            La commune dispose probablement d'un PLU ou d'un PLUi, mais il n'a
            pas (encore) été publié sur le{" "}
            <strong>Géoportail de l'Urbanisme</strong>. Vous pouvez fournir le
            règlement et le plan de zonage manuellement (récupérables sur le
            site de la mairie ou de l'EPCI), ou bien annuler — votre crédit
            n'a pas été décompté.
          </>
        ),
        hideRetry: true,
        allowUploadFallback: true,
      };
    case "zone-not-found":
      return {
        pillLabel: "Zone introuvable",
        title: "Zone PLU introuvable à ce point précis",
        body: (
          <>
            L'adresse a bien été géocodée, mais aucune zone PLU ne lui
            correspond exactement. Cela peut arriver près d'une frontière
            communale ou pour une adresse récente non encore couverte par le
            zonage publié. Vous pouvez réessayer, fournir le PLU
            manuellement, ou annuler — votre crédit n'a pas été décompté.
          </>
        ),
        allowUploadFallback: true,
      };
    default:
      return {
        pillLabel: "Analyse interrompue",
        title: "Une étape n'a pas pu aboutir",
        body: (
          <>
            L'analyse s'est arrêtée à l'étape{" "}
            <strong>« {stageLabel} »</strong>. Pas de panique : ça arrive
            quand un service externe (IGN, Géoplateforme, IA) est
            momentanément indisponible. Réessayez dans un instant.
          </>
        ),
      };
  }
}

export function PipelineErrorModal({
  stageLabel,
  errorMessage,
  errorCode,
  refundState,
  onRetry,
  onClose,
  onUploadFallback,
}: Props) {
  const { pillLabel, title, body, hideRetry, allowUploadFallback } =
    presentationFor(errorCode, stageLabel);
  const showUploadFallback = Boolean(allowUploadFallback && onUploadFallback);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pipeline-error-title"
      className="fixed inset-0 z-50 grid place-items-center bg-ink-900/40 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <Card
        className="max-w-[480px] w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <Pill tone="red" icon={<Ic.Cross size={12} />}>
            {pillLabel}
          </Pill>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-ink-100 grid place-items-center text-ink-500"
            aria-label="Fermer"
          >
            <Ic.Cross size={14} />
          </button>
        </div>
        <h3
          id="pipeline-error-title"
          className="mt-3 text-[20px] font-semibold tracking-tight"
        >
          {title}
        </h3>
        <p className="mt-2 text-[13.5px] text-ink-700 leading-relaxed">
          {body}
        </p>

        <RefundBanner state={refundState} />

        <details className="mt-4 text-[12px] text-ink-500">
          <summary className="cursor-pointer hover:text-ink-700">
            Détails techniques
          </summary>
          <div className="mt-2 p-3 bg-ink-50 rounded-md">
            <div className="text-ink-700">{errorMessage}</div>
            <div className="mt-1 font-mono text-[11px] text-ink-500">
              code : {errorCode}
            </div>
          </div>
        </details>

        <div className="mt-5 flex flex-col gap-2">
          {showUploadFallback && (
            <Button
              variant="primary"
              className="w-full"
              onClick={onUploadFallback}
              icon={<Ic.Bolt size={14} />}
            >
              Uploader le règlement et le plan
            </Button>
          )}
          {!hideRetry && (
            <Button
              variant={showUploadFallback ? "soft" : "dark"}
              className="w-full"
              onClick={onRetry}
              icon={<Ic.Bolt size={14} />}
            >
              Réessayer l'analyse
            </Button>
          )}
          <Button
            variant={
              showUploadFallback || !hideRetry ? "soft" : "dark"
            }
            className="w-full"
            onClick={onClose}
          >
            {showUploadFallback || !hideRetry ? "Annuler l'analyse" : "Fermer"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function RefundBanner({ state }: { state: RefundState }) {
  if (state === "skipped") return null;

  const tone =
    state === "refunded"
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : state === "failed"
        ? "bg-amber-50 text-amber-900 border-amber-200"
        : "bg-ink-50 text-ink-700 border-ink-200";

  const label =
    state === "pending"
      ? "Remboursement du crédit en cours…"
      : state === "refunded"
        ? "Votre crédit n'a pas été décompté."
        : "Le remboursement automatique a échoué — contactez-nous et nous régulariserons manuellement.";

  const icon =
    state === "pending" ? (
      <span className="inline-block w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
    ) : state === "refunded" ? (
      <Ic.Check size={14} />
    ) : (
      <Ic.Bolt size={14} />
    );

  return (
    <div
      className={`mt-4 flex items-center gap-2 px-3 py-2 rounded-md border text-[12.5px] ${tone}`}
    >
      {icon}
      <span>{label}</span>
    </div>
  );
}
